import type { ChargerConnectivity } from '@evi/shared';
import type { SpInstallBody, SpInstallConnectorType } from '@/lib/api';

/**
 * The Register Install POST body, built from the wizard's draft. Pure so the
 * payload can be tested without rendering the wizard.
 */
export interface SpInstallDraft {
  idempotencyKey: string;
  customerMode: 'existing' | 'new';
  tenantId: string;
  businessName: string;
  contactName: string;
  contactEmail: string;
  locationName: string;
  siteType: NonNullable<SpInstallBody['location']['site_type']> | '';
  street: string;
  city: string;
  state: string;
  zip: string;
  country: string;
  coordinates: { lat: number; lng: number } | null;
  placeId: string;
  /** Required: step 2 cannot advance until one is chosen. */
  connectivity: ChargerConnectivity;
  chargers: Array<{ serial: string; connector_types: SpInstallConnectorType[]; max_power_kw: string; pay_to_use: boolean }>;
  turnstileToken: string | null;
}

export function buildSpInstallBody(d: SpInstallDraft): SpInstallBody {
  return {
    idempotency_key: d.idempotencyKey,
    customer: d.customerMode === 'existing'
      ? { mode: 'existing', tenant_id: d.tenantId }
      : {
          mode: 'new',
          name: d.businessName.trim(),
          contact_name: d.contactName.trim(),
          contact_email: d.contactEmail.trim().toLowerCase(),
        },
    location: {
      name: d.locationName.trim(),
      address: {
        street: d.street.trim(),
        city: d.city.trim(),
        state: d.state.trim().toUpperCase(),
        zip: d.zip.trim(),
        ...(d.country.trim() ? { country: d.country.trim().toUpperCase() } : {}),
        ...(d.coordinates ? { coordinates: d.coordinates } : {}),
      },
      ...(d.placeId ? { google_place_id: d.placeId } : {}),
      ...(d.siteType ? { site_type: d.siteType } : {}),
    },
    chargers: d.chargers.map((c) => ({
      serial: c.serial.trim(),
      connector_type: c.connector_types[0] ?? 'J1772',
      connector_types: c.connector_types,
      max_power_kw: Number(c.max_power_kw),
      pay_to_use: c.pay_to_use,
    })),
    connectivity: d.connectivity,
    ...(d.turnstileToken ? { turnstile_token: d.turnstileToken } : {}),
  };
}
